// "Admin access" for one principal: which admin scopes it may request in an admin token (ADR-0028).
import { useState, type FormEvent } from "react";
import type { AdminScope, Principal } from "../api/types";
import { useServices } from "../components/context";
import { ErrorBanner, Section, useAction } from "../components/ui";
import { ADMIN_SCOPES, allowedScopes, hasWriteScope, sortScopes } from "../lib/adminScopes";

export function AdminAccess({
  tenantSlug,
  principal,
  onSaved,
  onClose,
}: {
  tenantSlug: string;
  principal: Principal;
  onSaved: () => void;
  onClose: () => void;
}) {
  const { api } = useServices();
  const [scopes, setScopes] = useState<AdminScope[]>(principal.admin_scopes ?? []);
  const save = useAction();
  const allowed = allowedScopes(principal);

  function toggle(s: AdminScope) {
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : sortScopes([...cur, s])));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const value = scopes.length ? sortScopes(scopes) : null;
    if (await save.run(async () => void (await api.setAdminScopes(tenantSlug, principal.id, value))))
      onSaved();
  }

  return (
    <Section title={`Admin access: ${principal.name}`}>
      <p className="muted">
        With admin scopes, this principal can get an admin token from the STS and use the admin API in this
        tenant, for example from a protected CI job. It can never manage members, owners, plans or admin
        scopes.
      </p>
      <form onSubmit={onSubmit} className="form">
        <fieldset>
          <legend>Scopes</legend>
          {ADMIN_SCOPES.map(({ scope, text }) => {
            const ok = allowed.includes(scope);
            return (
              <label key={scope} className="check">
                <input
                  type="checkbox"
                  checked={scopes.includes(scope)}
                  disabled={!ok}
                  onChange={() => toggle(scope)}
                />
                <span>
                  <code>{scope}</code> {text}
                </span>
              </label>
            );
          })}
        </fieldset>
        {principal.kind === "api_key" && (
          <p className="muted">
            An API key principal can have admin:read only. Use an OIDC or SPIFFE principal to write.
          </p>
        )}
        {scopes.includes("grants:write") && (
          <p className="banner warn" role="alert">
            grants:write is powerful: this principal can give itself publish or yank on any crate in this
            tenant.
          </p>
        )}
        {principal.kind === "oidc" && hasWriteScope(scopes) && (
          <p className="muted">
            A write scope on a GitLab or GitHub principal needs a protected-ref pin in its claims (GitLab
            ref_protected=true or environment; GitHub an exact ref or environment). The API refuses it without
            one.
          </p>
        )}
        <ErrorBanner error={save.error} />
        <div className="row">
          <button type="submit" disabled={save.busy}>
            Save admin access
          </button>
          <button type="button" className="link" onClick={onClose}>
            Close
          </button>
        </div>
      </form>
    </Section>
  );
}
